import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginAsAdmin, loginAsShop } from '../lib/pocketbase';

type LoginTab = 'company' | 'shop';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [loginTab, setLoginTab] = useState<LoginTab>('company');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (loginTab === 'company') {
        await loginAsAdmin(formData.email, formData.password);
      } else {
        await loginAsShop(formData.email, formData.password);
      }
      navigate('/');
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'shop_not_verified') {
        setError('حساب فروشگاه تأیید نشده یا دسترسی آن مسدود است');
      } else {
        setError('ایمیل یا رمز عبور اشتباه است');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTabChange = (tab: LoginTab) => {
    setLoginTab(tab);
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            ورود به پنل
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {loginTab === 'company'
              ? 'پنل مدیریت شرکت زرگرچی'
              : 'پنل فروشندگان — دسترسی به داشبورد و چت'}
          </p>
        </div>

        <div className="flex rounded-lg bg-white p-1 shadow-sm border border-gray-200">
          <button
            type="button"
            onClick={() => handleTabChange('company')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${
              loginTab === 'company'
                ? 'bg-primary-600 text-white shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            شرکت زرگرچی
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('shop')}
            className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-colors ${
              loginTab === 'shop'
                ? 'bg-primary-600 text-white shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            فروشندگان
          </button>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                ایمیل
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 input-field"
                placeholder="example@email.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                رمز عبور
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 input-field"
                placeholder="رمز عبور خود را وارد کنید"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  در حال ورود...
                </div>
              ) : (
                'ورود'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
